import React from 'react';
import ObjectiveQuestion from 'pages/new-questions/components/ObjectiveQuestion';
import ContextQuestion from 'pages/new-questions/components/ContextQuestion';
import DecisionTreeQuestion from 'pages/new-questions/components/DecisionTreeQuestion';
import CaseStudyQuestion from 'pages/new-questions/components/CaseStudyQuestion';
import {
  LectureQuestion,
  isObjectiveQuestion,
  isContextQuestion,
  isDecisionTreeQuestion,
  isCaseStudyQuestion,
  LectureAnswersState,
} from '../../types';
import { UserStepAnswer } from 'pages/new-questions/components/DecisionTreeQuestion/utils/types';
import { getEntryStepAnswers } from '../../utils/questionIdentity';

interface QuestionRendererProps {
  question: LectureQuestion;
  questionNumber?: number;
  totalQuestions?: number;
  answeredQuestionsCount?: number;
  answers: LectureAnswersState;
  onAnswerChange: (questionId: number, answer: any) => void;
  isReview?: boolean;
  Header?: React.ReactNode;
  BackButton?: React.ReactNode;
  onNextQuestion?: () => void;
  provaId?: number;
  caseStudyStepIndex?: number;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  totalQuestions,
  answeredQuestionsCount,
  answers,
  onAnswerChange,
  isReview = false,
  Header,
  BackButton,
  onNextQuestion,
  provaId,
  caseStudyStepIndex,
}) => {
  if (isObjectiveQuestion(question)) {
    const selectedAnswer = answers.objective[question.id_questao];

    return (
      <ObjectiveQuestion
        question={question}
        isTest={true}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        selectedAnswer={selectedAnswer}
        setSelectedAnswer={(answerId) => onAnswerChange(question.id_questao, answerId)}
        resolved={isReview}
        Header={Header}
        BackButton={BackButton}
      />
    );
  }

  if (isContextQuestion(question)) {
    const text = answers.context[question.id_questao] || '';

    return (
      <ContextQuestion
        question={question}
        isTest={true}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        answeredAnswer={text}
        setText={(newText) => onAnswerChange(question.id_questao, newText)}
        resolved={isReview}
        Header={Header}
        BackButton={BackButton}
        provaId={provaId}
      />
    );
  }

  if (isDecisionTreeQuestion(question)) {
    const userAnswers = answers.decisionTree[question.id_questao] || [];

    return (
      <DecisionTreeQuestion
        question={question}
        isTest={true}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        answeredQuestionsCount={answeredQuestionsCount}
        userAnswersAnswered={userAnswers}
        setUserAnswersAnswered={(newAnswers: UserStepAnswer[]) =>
          onAnswerChange(question.id_questao, newAnswers)
        }
        allowMultipleOnlyFlowWhenNoSingle={!question.step_single?.length}
        resolved={isReview}
        Header={Header}
        BackButton={BackButton}
        handleSubmit={onNextQuestion}
      />
    );
  }

  if (isCaseStudyQuestion(question)) {
    // Restrito às etapas desta entrada — o balde é compartilhado pelas N posições do caso
    const stepAnswers = getEntryStepAnswers(question, answers);

    return (
      <CaseStudyQuestion
        question={question}
        isTest={true}
        resolved={isReview}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        externalStepAnswers={stepAnswers}
        externalStepIndex={caseStudyStepIndex}
        onStepAnswerChange={(stepId, answerId) =>
          onAnswerChange(question.id_questao, { stepId, answerId })
        }
        Header={Header}
        BackButton={BackButton}
      />
    );
  }

  return null;
};
